import React from "react";
import { useTranslation } from "react-i18next";
import { PiFlipVerticalFill } from "react-icons/pi";
import { DEFAULT_DOOR_HEIGHT } from "../../../../features/floorplan3d/geometry";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { DoubleDoorType } from "../../../../types/floorplannerTypes";
import {
  resolveDoorHandleStyle,
  resolveDoorStyle,
} from "../../../../features/floorplan3d/symbols/openings/doors/door3DStyle";
import DimensionField from "../components/DimensionField";
import {
  DoorAppearanceOptionGrid,
  DoorHandleOptionGrid,
} from "../components/DoorStyleOptionGrid";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import MetricsSection from "../components/MetricsSection";
import SubsectionTitle from "../components/SubsectionTitle";
import ToggleRow from "../components/ToggleRow";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  areNumbersEqual,
  clampDoorAngleDegrees,
  degreesToRadians,
  getCommonMillimeterValue,
  getCommonValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";

type DoubleDoorSectionProps = Pick<
  PropertyPanelController,
  "updateGlobalState"
> & {
  selectedDoubleDoors: DoubleDoorType[];
  displayedDoubleDoors: DoubleDoorType[];
  is3DView: boolean;
};

const getDoubleDoorHeight = (door: DoubleDoorType) =>
  door.threeD?.height ?? DEFAULT_DOOR_HEIGHT;

/** Total opening width shown in the panel (two leaves). */
const getDoubleDoorOpeningWidth = (door: DoubleDoorType) =>
  (door.doubleDoorWidth || 0) * 2;

const DoubleDoorSection: React.FC<DoubleDoorSectionProps> = ({
  selectedDoubleDoors,
  displayedDoubleDoors,
  updateGlobalState,
  is3DView }) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  return (
    <>
      {displayedDoubleDoors.map((door) => {
        // Doors default unlocked; only lock when explicitly true.
        const aspectLocked = Boolean(door.lockAspectRatio);
        return (
        <div key={`doubleDoor-${door?.id}`}>
          <div className="border-b border-gray-300" />
          <div className="text-sm font-normal">
            <div className={panelObjectTitleRowClassName}>
              <UnitHeader
                title={
                  selectedDoubleDoors.length > 1
                    ? t("propertyPanel.multipleDoubleDoors", { count: selectedDoubleDoors.length })
                    : t("propertyPanel.doubleDoorTitle")
                }
              />
            </div>
            <MetricsSection>
              {is3DView ? (
                <LinkedDimensionsGroup
                  locked={aspectLocked}
                  onToggleLock={() => {
                    const newLockState = !aspectLocked;
                    selectedDoubleDoors.forEach((selectedDoor) => {
                      floorplannerStore.updateSymbolProperty(
                        selectedDoor.id,
                        "lockAspectRatio",
                        newLockState,
                      );
                      if (newLockState) {
                        const ratio =
                          (getDoubleDoorOpeningWidth(selectedDoor) || 1) /
                          (getDoubleDoorHeight(selectedDoor) || 1);
                        floorplannerStore.updateSymbolProperty(
                          selectedDoor.id,
                          "ratio",
                          ratio,
                        );
                      }
                    });
                  }}
                  widthField={
                    <DimensionField
                      label={t("propertyPanel.width")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedDoubleDoors.length > 1
                          ? (getCommonMillimeterValue(
                              selectedDoubleDoors,
                              getDoubleDoorOpeningWidth,
                            ) ?? "")
                          : toDisplayLength(getDoubleDoorOpeningWidth(door))
                      }
                      placeholder={
                        selectedDoubleDoors.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) => {
                        const value = parseFloat(nextValue);
                        if (!Number.isFinite(value)) return;
                        updateGlobalState(
                          selectedDoubleDoors,
                          "doubleDoorWidth",
                          value / 2,
                        );
                        if (aspectLocked) {
                          const currentWidth = getDoubleDoorOpeningWidth(door);
                          if (currentWidth > 0) {
                            const scale =
                              toWorldFromDisplay(value) / currentWidth;
                            updateGlobalState(
                              selectedDoubleDoors,
                              "symbol3DHeight",
                              toDisplayLength(
                                getDoubleDoorHeight(door) * scale,
                              ),
                            );
                          }
                        }
                      }}
                    />
                  }
                  lengthField={
                    <DimensionField
                      label={t("propertyPanel.height")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedDoubleDoors.length > 1
                          ? (getCommonMillimeterValue(
                              selectedDoubleDoors,
                              getDoubleDoorHeight,
                            ) ?? "")
                          : toDisplayLength(getDoubleDoorHeight(door))
                      }
                      placeholder={
                        selectedDoubleDoors.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) => {
                        const value = parseFloat(nextValue);
                        if (!Number.isFinite(value)) return;
                        updateGlobalState(
                          selectedDoubleDoors,
                          "symbol3DHeight",
                          value,
                        );
                        if (aspectLocked) {
                          const currentHeight = getDoubleDoorHeight(door);
                          if (currentHeight > 0) {
                            const scale =
                              toWorldFromDisplay(value) / currentHeight;
                            updateGlobalState(
                              selectedDoubleDoors,
                              "doubleDoorWidth",
                              toDisplayLength(
                                getDoubleDoorOpeningWidth(door) * scale,
                              ) / 2,
                            );
                          }
                        }
                      }}
                    />
                  }
                />
              ) : (
                <div className="grid items-end gap-2 py-1 grid-cols-1">
                  <DimensionField
                    label={t("propertyPanel.width")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selectedDoubleDoors.length > 1
                        ? (getCommonMillimeterValue(
                            selectedDoubleDoors,
                            getDoubleDoorOpeningWidth,
                          ) ?? "")
                        : toDisplayLength(getDoubleDoorOpeningWidth(door))
                    }
                    placeholder={
                      selectedDoubleDoors.length > 1 ? mixed : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedDoubleDoors,
                        "doubleDoorWidth",
                        parseFloat(nextValue) / 2,
                      )
                    }
                  />
                </div>
              )}
            </MetricsSection>
            {is3DView && (
              <>
                <SubsectionTitle>
                  {t("propertyPanel.doorAppearance")}
                </SubsectionTitle>
                <DoorAppearanceOptionGrid
                  value={
                    selectedDoubleDoors.length > 1
                      ? (getCommonValue(selectedDoubleDoors, (symbol) =>
                          resolveDoorStyle(symbol.threeD?.doorStyle),
                        ) ?? "")
                      : resolveDoorStyle(door.threeD?.doorStyle)
                  }
                  mixedLabel={mixed}
                  onChange={(style) =>
                    updateGlobalState(selectedDoubleDoors, "doorStyle", style)
                  }
                />
                <SubsectionTitle>
                  {t("propertyPanel.doorHandles")}
                </SubsectionTitle>
                <DoorHandleOptionGrid
                  value={
                    selectedDoubleDoors.length > 1
                      ? (getCommonValue(selectedDoubleDoors, (symbol) =>
                          resolveDoorHandleStyle(
                            symbol.threeD?.doorHandleStyle,
                          ),
                        ) ?? "")
                      : resolveDoorHandleStyle(door.threeD?.doorHandleStyle)
                  }
                  mixedLabel={mixed}
                  onChange={(style) =>
                    updateGlobalState(
                      selectedDoubleDoors,
                      "doorHandleStyle",
                      style,
                    )
                  }
                />
              </>
            )}
            {!is3DView && <div className="border-b border-gray-300" />}
            <div className="p-3 flex items-center justify-between py-2">
              <DimensionField
                label={t("propertyPanel.openAngle")}
                min={0}
                max={90}
                step={1}
                value={
                  selectedDoubleDoors.length > 1
                    ? (getCommonValue(
                        selectedDoubleDoors,
                        (symbol) =>
                          radiansToRoundedDegrees(symbol.openAngle || 0),
                        areNumbersEqual,
                      ) ?? "")
                    : radiansToRoundedDegrees(door?.openAngle || 0)
                }
                placeholder={
                  selectedDoubleDoors.length > 1 ? mixed : undefined
                }
                onChange={(nextValue) =>
                  updateGlobalState(
                    selectedDoubleDoors,
                    "openAngle",
                    degreesToRadians(
                      clampDoorAngleDegrees(parseFloat(nextValue) || 0),
                    ),
                  )
                }
              />
            </div>
            <div className="border-b border-gray-300" />
            <div className="p-3 pl-4 flex items-center justify-between">
              <div>{t("propertyPanel.doorHanging")}</div>
            </div>
            <div className="p-3 items-left py-2">
              <ToggleRow
                icon={<PiFlipVerticalFill />}
                label={t("propertyPanel.flopDoorBlade")}
                checked={
                  getCommonValue(
                    selectedDoubleDoors,
                    (symbol) => symbol.flipVertical || false,
                  ) ?? false
                }
                onChange={(checked) =>
                  updateGlobalState(selectedDoubleDoors, "flipVertical", checked)
                }
              />
            </div>
          </div>
          <div className="border-b border-gray-300" />
        </div>
        );
      })}
    </>
  );
};

export default DoubleDoorSection;
